import { ChangeDetectionStrategy, Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs';
import { ProgressTrackerService } from 'src/app/_shared/components/progress/services/progress-tracker.service';
import { ProgressOperationConfig, ProgressState, StepState } from 'src/app/_shared/components/progress/models/progress-definitions';

@Component({
    selector: 'app-progress-overlay',
    templateUrl: './progress-overlay.component.html',
    styleUrls: ['./progress-overlay.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProgressOverlayComponent {

    /**
     * Filtre par catégorie d'opération.
     * Si défini, n'affiche que les opérations de ces catégories.
     * Ex: ['export'] pour n'afficher que les exports.
     */
    @Input() categories: string[] | null = null;

    /**
     * Observable du state (fourni par la directive ou par défaut via le service)
     */
    @Input() state$: Observable<ProgressState>;

    /**
     * Observable de la config (fourni par la directive ou par défaut via le service)
     */
    @Input() config$: Observable<ProgressOperationConfig | null>;

    private readonly tracker = inject(ProgressTrackerService);

    constructor() {
        // Valeurs par défaut si utilisé directement dans un template (rétro-compatibilité)
        this.state$ = this.tracker.progress$;
        this.config$ = this.tracker.currentConfig$;
    }

    getStepState(stepIndex: number, state: ProgressState): StepState {
        if (state.status === 'error' && stepIndex === state.currentStepIndex) {
            return StepState.Error;
        }
        if (stepIndex < state.currentStepIndex) {
            return StepState.Completed;
        }
        if (stepIndex === state.currentStepIndex) {
            return StepState.Running;
        }
        return StepState.Pending;
    }

    shouldDisplay(config: ProgressOperationConfig): boolean {
        if (!this.categories || this.categories.length === 0) {
            return true;
        }
        return this.categories.includes(config.category);
    }

    /** Détermine si l'overlay doit être affiché (en cours, terminé ou en erreur) */
    isVisible(state: ProgressState, config: ProgressOperationConfig): boolean {
        const visibleStatuses = ['running', 'completed', 'error'];
        return visibleStatuses.includes(state.status) && this.shouldDisplay(config);
    }
}
